import React, { useState } from "react";
import { Badge, Button, Card, EmptyState } from "../../../../ui";
import { Actions } from "./ui-pending";
import "./BookingPreview.scss";
import { toast } from "react-toastify";
import {
  studentClassSchedule,
  updateOnboardingStatus,
  assignOnboardingTeacher,
  studentCourses,
} from "../../../../Api/apiUtils";
import BookingSuccessScreen, { type BookingSuccessMode } from "./BookingSuccessScreen";
import { maybePostTeacherAssignmentAlert } from "./findTeacher";

export type PreviewSlot = {
  teacherSlotInfoId: number;
  startTime: string;
  endTime: string;
};

export type PreviewDateEntry = {
  date: string;
  dayOfWeek: string;
  slots: PreviewSlot[];
};

export type PreviewScheduleResponse = {
  teacherId?: number;
  scheduleType?: string;
  totalClasses?: number;
  remainingClasses?: number;
  timezone?: string;
  dates?: PreviewDateEntry[];
  softHoldDates?: PreviewDateEntry[];
  softHoldCount?: number;
};

type MatchedSlot = { day: string; time: string };

type BookingPreviewProps = {
  teacher: {
    firstName?: string;
    lastName?: string;
    payoutCategory?: string;
    teacherId?: string | number;
    credentialId?: string | number;
  };
  matchedSlots: MatchedSlot[];
  preview: PreviewScheduleResponse;
  studentId: string | number;
  studentUserId: string | number;
  courseId: string | number;
  enrollmentId?: string | number | null;
  scheduleType?: string;
  onBack: () => void;
  onAssignTeacherOnly?: () => void;
  onBookSuccess?: () => void;
  fetchCourses?: () => Promise<void> | void;
  availableTeachersForAlert?: Array<{
    payoutCategory?: string;
    activeStudents?: string | number;
    activeStudentCount?: string | number;
  }>;
};

function formatTime(time?: string): string {
  if (!time) return "";
  const [hStr, mStr] = String(time).split(":");
  let hour = parseInt(hStr, 10);
  const minutes = (mStr || "00").slice(0, 2);
  if (Number.isNaN(hour)) return time;
  const meridiem = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${meridiem}`;
}

function formatDayShort(dayOfWeek: string, dateStr: string): string {
  if (dayOfWeek) {
    return dayOfWeek.slice(0, 1) + dayOfWeek.slice(1, 3).toLowerCase();
  }
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-GB", { weekday: "short" });
  } catch {
    return "";
  }
}

function formatDateParts(dateStr: string): { dayNum: string; monthYear: string } {
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return {
      dayNum: String(date.getDate()),
      monthYear: date.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
    };
  } catch {
    return { dayNum: dateStr, monthYear: "" };
  }
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { message?: string } | string };
    message?: string;
  };
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object" && typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if (typeof err?.message === "string" && err.message.trim() && !err.message.startsWith("Request failed")) {
    return err.message.trim();
  }
  return fallback;
}

async function resolveEnrollmentId(
  studentId: string | number,
  courseId: string | number,
  provided?: string | number | null
): Promise<string | number | null> {
  if (provided != null && String(provided).trim() !== "") {
    return provided;
  }

  try {
    const courses = await studentCourses(studentId);
    const list = Array.isArray(courses) ? courses : [];
    const match = list.find(
      (c: any) =>
        String(c?.courseId ?? c?.paidCourseId ?? "") === String(courseId) &&
        !c?.isDemoCourse
    );
    return (
      match?.courseEnrollmentId ??
      match?.enrollmentId ??
      list.find((c: any) => String(c?.courseId) === String(courseId))?.courseEnrollmentId ??
      null
    );
  } catch (error) {
    console.error("Failed to resolve enrollmentId:", error);
    return null;
  }
}

const BookingPreview: React.FC<BookingPreviewProps> = ({
  teacher,
  matchedSlots,
  preview,
  studentId,
  studentUserId,
  courseId,
  enrollmentId: enrollmentIdProp,
  scheduleType = "fixed",
  onBack,
  onAssignTeacherOnly,
  onBookSuccess,
  fetchCourses,
  availableTeachersForAlert = [],
}) => {
  const [confirming, setConfirming] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [successMode, setSuccessMode] = useState<BookingSuccessMode | null>(null);

  const purchasedDates = preview.dates || [];
  const purchasedCount = preview.totalClasses ?? purchasedDates.length;
  const unscheduledCount = preview.remainingClasses ?? purchasedDates.length;
  const bookingCount = purchasedDates.length;
  const timezone = preview.timezone || "";

  const teacherName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "Teacher";
  const level = String(teacher.payoutCategory || "").trim().toUpperCase();
  const assignTeacherId = preview.teacherId ?? teacher.teacherId;

  const refreshCourses = async () => {
    try {
      if (fetchCourses) {
        await fetchCourses();
      } else {
        await studentCourses(studentId);
      }
    } catch (error) {
      console.error("Failed to refresh courses after booking/assign:", error);
    }
  };

  const handleAssignTeacherOnly = async () => {
    if (assignTeacherId == null || String(assignTeacherId).trim() === "") {
      toast.error("Teacher id is missing.");
      return;
    }

    setAssigning(true);
    try {
      const enrollmentId = await resolveEnrollmentId(studentId, courseId, enrollmentIdProp);
      if (enrollmentId == null || String(enrollmentId).trim() === "") {
        toast.error("Enrollment id was not found.");
        return;
      }

      await assignOnboardingTeacher(enrollmentId, assignTeacherId);
      await maybePostTeacherAssignmentAlert({
        enrollmentId,
        teacherId: assignTeacherId,
        assignedPayoutCategory: teacher.payoutCategory,
        availableTeachers: availableTeachersForAlert,
      });
      try {
        // Assign-only leaves booking for later — mark TC reboarding pending.
        await updateOnboardingStatus(enrollmentId, "tc_reboarding_pending");
      } catch (statusError) {
        console.error("Teacher assigned but onboarding status update failed:", statusError);
        toast.warning("Teacher assigned, but failed to mark TC reboarding pending.");
        await refreshCourses();
        setSuccessMode("assigned");
        return;
      }

      await refreshCourses();
      setSuccessMode("assigned");
    } catch (error) {
      console.error("Error assigning teacher:", error);
      toast.error(getApiErrorMessage(error, "Failed to assign teacher. Please try again."));
    } finally {
      setAssigning(false);
    }
  };

  const handleConfirmBook = async () => {
    const selectedSlots = purchasedDates.flatMap((entry) =>
      (entry.slots || []).map((slot) => ({ teacherSlotInfoId: slot.teacherSlotInfoId }))
    );

    if (!selectedSlots.length) {
      toast.error("No slots available to book.");
      return;
    }
    if (!studentUserId || !courseId) {
      toast.error("Student or course is missing.");
      return;
    }

    setConfirming(true);
    try {
      await studentClassSchedule({
        studentUserId: Number(studentUserId) || studentUserId,
        courseId: Number(courseId) || courseId,
        scheduleType,
        selectedSlots,
      });

      const enrollmentId = await resolveEnrollmentId(studentId, courseId, enrollmentIdProp);
      if (enrollmentId != null && String(enrollmentId).trim() !== "") {
        await maybePostTeacherAssignmentAlert({
          enrollmentId,
          teacherId: assignTeacherId,
          assignedPayoutCategory: teacher.payoutCategory,
          availableTeachers: availableTeachersForAlert,
        });
        try {
          await updateOnboardingStatus(enrollmentId, "onboarding_completed");
        } catch (statusError) {
          console.error("Classes booked but onboarding status update failed:", statusError);
          toast.warning("Classes booked, but failed to mark onboarding completed.");
          await refreshCourses();
          setSuccessMode("booked");
          return;
        }
      } else {
        toast.warning("Classes booked, but enrollment id was not found for onboarding status.");
        await refreshCourses();
        setSuccessMode("booked");
        return;
      }

      await refreshCourses();
      setSuccessMode("booked");
    } catch (error) {
      console.error("Error confirming book all classes:", error);
      toast.error("Failed to book classes. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  if (successMode) {
    return (
      <BookingSuccessScreen
        mode={successMode}
        teacher={teacher}
        matchedSlots={matchedSlots}
        preview={preview}
        scheduleType={scheduleType}
        onDone={() => {
          if (successMode === "assigned") {
            onAssignTeacherOnly?.();
          } else {
            onBookSuccess?.();
          }
        }}
      />
    );
  }

  return (
    <Card
      title="Booking Preview"
      actions={
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back to teacher list
        </Button>
      }
    >
      <div className="booking-preview">
        <div className="booking-preview__hero">
          <div className="booking-preview__teacher">
            <span className="booking-preview__teacher-name">{teacherName}</span>
            <span className="booking-preview__teacher-meta">
              {level ? <Badge tone="success">{level}</Badge> : null}
              {timezone ? <span className="ds-text-subtle">{timezone}</span> : null}
            </span>
          </div>

          <div className="booking-preview__pattern">
            <span className="booking-preview__pattern-label">Recurring weekly</span>
            <div className="booking-preview__chips">
              {matchedSlots.map((slot) => (
                <span key={`${slot.day}-${slot.time}`} className="booking-preview__chip">
                  <span className="booking-preview__chip-day">
                    {slot.day.slice(0, 1) + slot.day.slice(1, 3).toLowerCase()}
                  </span>
                  <span className="booking-preview__chip-time">{formatTime(slot.time)}</span>
                </span>
              ))}
              {!matchedSlots.length && (
                <span className="ds-text-subtle">No recurring slot selected</span>
              )}
            </div>
          </div>
        </div>

        {!purchasedDates.length ? (
          <EmptyState
            title="No classes in preview."
            description="Go back to the teacher list to pick another teacher or slot."
          />
        ) : (
          <div className="booking-preview__schedule">
            <div className="booking-preview__schedule-head">
              <span className="booking-preview__schedule-title">Schedule</span>
              <span className="booking-preview__counts" aria-label="Classes summary">
                <span>
                  <strong>{bookingCount}</strong> booking
                </span>
                <span className="booking-preview__counts-sep" aria-hidden>
                  ·
                </span>
                <span>
                  <strong>{unscheduledCount}</strong> left
                </span>
                <span className="booking-preview__counts-sep" aria-hidden>
                  ·
                </span>
                <span>
                  <strong>{purchasedCount}</strong> package
                </span>
              </span>
            </div>
            <ul className="booking-preview__list">
              {purchasedDates.map((entry, index) => {
                const startTime = entry.slots?.[0]?.startTime;
                const { dayNum, monthYear } = formatDateParts(entry.date);
                return (
                  <li key={`paid-${entry.date}-${index}`} className="booking-preview__row">
                    <span className="booking-preview__row-index ds-num">{index + 1}</span>
                    <span className="booking-preview__row-day">
                      {formatDayShort(entry.dayOfWeek, entry.date)}
                    </span>
                    <span className="booking-preview__row-date">
                      <span className="booking-preview__row-daynum">{dayNum}</span>
                      <span className="ds-text-muted">{monthYear}</span>
                    </span>
                    {startTime ? (
                      <Badge tone="primary">{formatTime(startTime)}</Badge>
                    ) : (
                      <span className="ds-text-subtle">—</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <Actions className="booking-preview__actions">
          <Button
            variant="secondary"
            onClick={() => void handleAssignTeacherOnly()}
            disabled={confirming}
            loading={assigning}
          >
            {assigning ? "Assigning..." : "Assign Teacher Only"}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConfirmBook()}
            disabled={assigning || unscheduledCount === 0 || purchasedDates.length === 0}
            loading={confirming}
          >
            {confirming
              ? "Booking..."
              : bookingCount > 0
                ? `Confirm & Book ${bookingCount} Class${bookingCount === 1 ? "" : "es"}`
                : "Confirm & Book All Classes"}
          </Button>
        </Actions>
      </div>
    </Card>
  );
};

export default BookingPreview;
